import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';

const VerificationCompletePage = () => {
  const navigate = useNavigate();

  const handleViewProfile = () => {
    navigate('/profile');
  };

  const handleBrowseJobs = () => {
    navigate('/jobs');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Animation */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-500 to-blue-600 rounded-full mb-6 animate-bounce">
            <Icon name="verified" size={48} className="text-white" />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">
            🎉 Congratulations!
          </h1>
          <p className="text-2xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Your profile has been successfully verified!
          </p>
        </div>

        {/* Main Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
          {/* Verified Badge Display */}
          <div className="text-center mb-8 pb-8 border-b border-gray-200 dark:border-gray-700">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 px-8 py-4 rounded-full mb-4">
              <Icon name="verified" size={32} className="text-green-600" />
              <span className="text-2xl font-bold text-gray-900 dark:text-white">
                VERIFIED
              </span>
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              This badge will now appear on your profile
            </p>
          </div>

          {/* What This Means */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              What This Means For You
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
                  <Icon name="trending_up" size={32} className="text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  3x More Visibility
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Your profile will rank higher in recruiter searches
                </p>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                  <Icon name="workspace_premium" size={32} className="text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Trust Badge
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Stand out with verified credentials
                </p>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
                  <Icon name="stars" size={32} className="text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Priority Access
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Get noticed by top companies first
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Icon name="lightbulb" size={24} className="text-yellow-500" />
              What's Next?
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Icon name="check_circle" size={20} className="text-green-600 mt-1" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Your verified badge is now live
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    It will appear on your profile, job applications, and search results
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="check_circle" size={20} className="text-green-600 mt-1" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Recruiters can now see you're verified
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    This builds trust and increases your chances of being contacted
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="check_circle" size={20} className="text-green-600 mt-1" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Start applying to jobs with confidence
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Your verified status gives you an edge over other candidates
                  </p>
                </div>
              </li>
            </ul>
          </div>

          {/* Privacy Note */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <Icon name="lock" size={24} className="text-gray-600 dark:text-gray-400 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Your Privacy Matters
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Your assessment scores are private and only visible to our system administrators. 
                  Only the "Verified" badge is shown publicly. Your detailed evaluation helps us 
                  match you with the right opportunities.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={handleViewProfile}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Icon name="person" size={20} />
              View My Profile
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={handleBrowseJobs}
              className="flex items-center gap-2"
            >
              <Icon name="work" size={20} />
              Browse Jobs
            </Button>
          </div>
        </div>

        {/* Celebration Message */}
        <div className="text-center">
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">
            🌟 You're now part of our verified community! 🌟
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Share your achievement and start exploring opportunities
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerificationCompletePage;
